import { noteRefusedRemoved, noteRowAdded, noteRowRemoved, outboxDb } from './db'
import type { OutboxEntry } from './db'

/**
 * Writes one command to the outbox (design §8.2). The caller — `api/
 * hooks.ts`'s `commandHook`, on a genuine network failure for an
 * `offline: true` shape — supplies an `OutboxEntry` whose `idempotencyKey`
 * it ALREADY minted with `newUlid()` for the very request that just failed
 * to reach the server. This function never mints its own key and never
 * touches `idempotencyKey` once written.
 *
 * That single fact is the entire safety property `drain.test`'s "draining
 * the same entry twice appends one event" case measures: at-least-once
 * delivery is safe ONLY because a replayed command carries the identical
 * key the appender's `command_log` dedupes against — mint a fresh one here
 * or in `drain.ts` instead of reusing the stored one, and a retry becomes a
 * second, indistinguishable command.
 *
 * `put`, not `add`: a caller enqueueing under a key that already has a row
 * (the realistic case is `drain.ts` re-queuing after a business refusal is
 * superseded, or a test deliberately re-enqueuing the same entry to prove
 * the idempotency property above) overwrites that row rather than throwing
 * `ConstraintError` — there is exactly one row per command, by construction,
 * never two competing for the same key.
 */
export async function enqueue(entry: OutboxEntry): Promise<void> {
  const table = outboxDb().entries
  const existed = (await table.get(entry.idempotencyKey)) !== undefined
  await table.put(entry)
  // Only a genuinely NEW row changes how many rows the table holds — an
  // overwrite of an existing key (see the `put` note above) must not double
  // count it, or `outboxIsEmpty()` (`db.ts`) would report a row that does
  // not exist.
  if (!existed) noteRowAdded()
}

/**
 * Every row still awaiting a send, oldest first. `drain.ts`'s own source of
 * what to send; also the read Task 18's Sync tray will use to list queued
 * work (not built here — Task 18's job).
 *
 * `status === 'queued'` only — a `refused` row is done being sent (it will
 * not be retried on its own; a human resolves it via Task 18's tray), so it
 * has nothing to do with "what should `drain()` try next".
 */
export async function pending(): Promise<OutboxEntry[]> {
  return outboxDb().entries.where('status').equals('queued').sortBy('enqueuedAt')
}

/**
 * Every row a drain has given up on and marked `refused` — Task 18's Sync
 * tray reads this to fill "Needs your attention". The reader `drain()`
 * (Task 17) needed and never got: `pending()` above deliberately filters
 * `refused` rows OUT (that file's own comment), so before this task nothing
 * could ever list them again once `drain()` had set them aside — the tray
 * had a status to write but no way to show what it meant.
 *
 * Oldest first, matching `pending()`'s own ordering: the entry that has been
 * sitting unresolved longest is the one most worth surfacing first.
 */
export async function refused(): Promise<OutboxEntry[]> {
  return outboxDb().entries.where('status').equals('refused').sortBy('enqueuedAt')
}

/**
 * The discard Task 17 left out (that task's own report: "There is no ...
 * discard for `refused` rows ... otherwise a single refusal pins a kiosk's
 * device-mode auto-accept off permanently"). Called once a `refused` entry
 * has been handled — either a registered `RepairComponent`'s own resubmission
 * reached the server (`outbox/repair.tsx`'s `RefusedEntry`), or a human
 * dismissed it outright with nothing to resend. Either way, the ORIGINAL
 * entry's job — holding the refusal until a human saw it — is done; this is
 * what lets it actually leave the table rather than sitting there forever
 * once seen.
 *
 * CRITICAL, fixed here after being measured (fix round 1): the get-then-
 * delete used to be TWO separate Dexie calls, each its own implicit
 * transaction, with nothing serialising one `discard()` call against
 * another for the SAME key. Two clicks on "Dismiss" close enough together —
 * a real, reachable double-tap on a small control, not a contrived race —
 * both read the row via `get()` before either had deleted it, both then
 * deleted (the second a harmless no-op against an already-gone key,
 * `Table.delete` does not complain), and BOTH called `noteRefusedRemoved()`
 * — the badge dropped by 2 for one row leaving, understating "how many
 * still need a human" until the next full reload re-hydrates the mirror
 * from the real table. Measured directly: two overlapping `discard()` calls
 * against one refused row left `outboxCountsSnapshot().refused` at `-1`
 * (clamped nowhere) instead of `0`, and — with a SECOND refused row still
 * present — the badge read `0` while a genuinely unresolved entry remained.
 *
 * Fixed by wrapping the get, the delete AND the counter decision in ONE
 * Dexie `'rw'` transaction. This is not merely tidier: a `'readwrite'`
 * IndexedDB transaction against a given object store cannot interleave with
 * ANOTHER `'readwrite'` transaction against the SAME store — the browser
 * queues the second until the first commits. So the second `discard()`
 * call's OWN `get()`, running inside its own transaction, only starts once
 * the first transaction (get + delete + decrement) has fully committed, and
 * sees the row already gone — `if (!entry) return` then skips the decrement
 * entirely, exactly once per genuine removal. Verified empirically (not
 * merely reasoned about), firing `Promise.all([discard(key), discard(key)])`
 * against ONE refused row: `refusedCount` lands at exactly `0`, not `-1` —
 * see `test/outbox.test.tsx`'s "discard() is safe under concurrency"
 * describe block for the mutation this catches (delete the
 * `db.transaction(...)` wrapper and this drops to `-1`).
 */
export async function discard(idempotencyKey: string): Promise<void> {
  const db = outboxDb()
  await db.transaction('rw', db.entries, async () => {
    const entry = await db.entries.get(idempotencyKey)
    if (!entry) return
    await db.entries.delete(idempotencyKey)
    if (entry.status === 'queued') noteRowRemoved()
    else noteRefusedRemoved()
  })
}
